import Dialog from "../common/Dialog";
import { Button } from "../ui/button";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import TrashCanSvg from "../../assets/v2/svg/RedTrash.svg";
import DownloadSvg from "../../assets/svg/DownLoad.svg";
import { Task } from "@/api/mutation/useTaskCreateMutation";
import useRemoveTaskAttachmentMutation from "@/api/mutation/useTaskAttechmentRemoveMutation";
import { errorConfig } from "@/lib/toastError";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import useAttachmentUpdateMutation from "@/api/mutation/useAttachmentUpdateMutation";
import { useFormik } from "formik";
import { taskAttachmentSchema } from "@backend/src/schemas/taskSchema";
import { toFormikValidationSchema } from "zod-formik-adapter";
import GreenChackMark from "@/assets/svg/GreenChackMark.svg";
import crossNew from "@/assets/svg/CrossIcon.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
type Props = {
  task: Task | undefined;
  refetch: () => void;
  allowed?: boolean;
};

import { Link2, Pencil } from "lucide-react";
import InputText from "../common/InputText";
import { useTaskLinkAttachmentUpdateMutation } from "@/api/mutation/useTaskLinkAttachmentUpdateMutation";

/** True if attachment should open in browser (link icon) like SharePoint/Google Drive, not download */
function isOpenInBrowserAttachment(item: {
  label?: string;
  url?: string;
}): boolean {
  if (item.label === "link") return true;
  const url = (item.url || "").toLowerCase();
  return (
    url.includes("sharepoint.com") ||
    url.includes("onedrive.live.com") ||
    url.includes("drive.google.com")
  );
}

/** True if URL is a local/file path (file:// or UNC) - these are blocked when clicked from https pages */
function isLocalOrFileUrl(url: string): boolean {
  if (!url) return false;
  const u = url.trim().toLowerCase();
  return u.startsWith("file:") || u.startsWith("\\\\");
}

/** Convert URL to a path suitable for clipboard / File Explorer (UNC or local path) */
function getPathForExplorer(url: string): string {
  if (!url) return "";
  const trimmed = url.trim();
  // Already UNC
  if (trimmed.startsWith("\\\\")) return trimmed.replace(/\//g, "\\");
  // file://host/path -> \\host\path  (network path)
  if (
    trimmed.toLowerCase().startsWith("file://") &&
    !trimmed.toLowerCase().startsWith("file:///")
  ) {
    const withoutScheme = trimmed.slice(7).replace(/\//g, "\\");
    return "\\\\" + withoutScheme;
  }
  // file:///C:/path -> C:\path (local drive)
  if (trimmed.toLowerCase().startsWith("file:///")) {
    const withoutScheme = trimmed.slice(8).replace(/\//g, "\\");
    return withoutScheme;
  }
  return trimmed;
}

function TaskAttachment(props: Props) {
  const removeTaskAttachmentMutation = useRemoveTaskAttachmentMutation();
  const [showAllAttachment, setShowAllAttachment] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(
    null,
  );
  const [linkToEdit, setLinkToEdit] = useState<{
    id: string;
    name: string;
    url: string;
  } | null>(null);
  const { mutate: updateLink, isPending: isUpdatingLink } =
    useTaskLinkAttachmentUpdateMutation(props.task?.taskId);

  const { t } = useTranslation();
  const removeConfirm = () => {
    if (showConfirmDelete) {
      removeTaskAttachmentMutation.mutate(showConfirmDelete, {
        onSuccess(data) {
          props.refetch();
          toast.success(data.data.message);
          setShowConfirmDelete(null);
        },
        onError(error) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                  "You are not authorized to edit tasks which are not assigned to you",
                )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
          setShowConfirmDelete(null);
        },
      });
    }
  };
  const formik = useFormik({
    initialValues: {
      label: "",
    },
    validationSchema: toFormikValidationSchema(taskAttachmentSchema),
    onSubmit(values) {
      attachmentUpdateMutation.mutate(values, {
        onSuccess(data) {
          props.refetch();
          setLabel("");
          toast.success(data.data.message);
        },
        onError(error) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                  "You are not authorized to edit tasks which are not assigned to you",
                )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
        },
      });
    },
  });
  const [label, setLabel] = useState<string>("");
  const attachmentUpdateMutation = useAttachmentUpdateMutation(label);

  const handleEditLinkSubmit = () => {
    if (linkToEdit && linkToEdit.name && linkToEdit.url) {
      updateLink(
        {
          attachmentId: linkToEdit.id,
          name: linkToEdit.name,
          url: linkToEdit.url,
        },
        {
          onSuccess: () => {
            toast.success(t("link_updated_success"));
            setLinkToEdit(null);
            props.refetch();
          },
          onError: () => {
            toast.error(t("link_update_failed"));
          },
        },
      );
    }
  };
  const isWebUrl = (url: string) => /^(https?:)?\/\/.+/i.test(url);
  const [editLinkType, setEditLinkType] = useState<"WEB" | "LOCAL">("WEB");

  const isLocalPath = (url: string) =>
    /^(\\\\[^\\]+\\[^\\]+|\/(?!\/)|\.\/|\.\.\/|file:\/\/)/i.test(url);

  const isValidEditLink =
    (editLinkType === "WEB" && isWebUrl(linkToEdit?.url || "")) ||
    (editLinkType === "LOCAL" && isLocalPath(linkToEdit?.url || ""));
  useEffect(() => {
    if (linkToEdit?.url) {
      if (isWebUrl(linkToEdit.url)) setEditLinkType("WEB");
      else setEditLinkType("LOCAL");
    }
  }, [linkToEdit]);

  const getOpenableUrl = (url: string) => {
    if (!url) return url;
    if (url.startsWith("\\\\")) {
      return "file://///" + url.replace(/\\/g, "/");
    }
    return url;
  };

  const handleLinkIconClick = (
    e: React.MouseEvent,
    item: { url?: string; name?: string },
  ) => {
    const url = item.url?.trim();
    if (!url) return;
    if (isLocalOrFileUrl(url)) {
      e.preventDefault();
      e.stopPropagation();
      const pathForExplorer = getPathForExplorer(url);
      const openableUrl = getOpenableUrl(url);
      const copied = pathForExplorer || openableUrl;
      navigator.clipboard
        .writeText(copied)
        .then(() => {
          toast.success(
            t("path_copied_paste_in_explorer_or_run") ||
              "Path copied. Paste in File Explorer address bar or Run (Win+R) to open.",
              {
                autoClose: false,
              }
          );
        })
        .catch(() => {
          toast.error(t("copy_failed") || "Copy failed.");
        });
      // Try opening in new tab anyway (works in some environments, e.g. Electron or file:// origin)
      try {
        window.open(openableUrl, "_blank", "noopener");
      } catch {
        // Ignore; clipboard copy is the main fallback
      }
    }
    // For web URLs, let the default <a> behavior handle it (no preventDefault)
  };
  return (
    <div className="max-h-52 overflow-auto px-2 mt-3">
      {props.task?.documentAttachments
        .slice(
          0,
          showAllAttachment ? props.task?.documentAttachments.length : 5,
        )
        .map((item) => {
          const isLink = isOpenInBrowserAttachment(item);
          return (
            <div className="w-full flex justify-between gap-2 bg-[#FAFAFB] items-center lex mt-2 px-1.5 py-3 text-sm rounded-md !bg-primary-50">
              <div className="flex items-center gap-1 text-nowrap truncate max-w-[50%]">
                <a
                  href={getOpenableUrl(item.url || "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-90 flex gap-1 items-center tooltip"
                  onClick={(e) => {
                    if (isLocalOrFileUrl(item.url || "")) {
                      handleLinkIconClick(e, item);
                    }
                  }}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {isLink ? (
                          <Link2 className="w-3.5 h-3.5 text-primary-500" />
                        ) : (
                          <img
                            src={DownloadSvg}
                            className="min-w-3.5 w-3.5"
                            alt=""
                          />
                        )}
                      </TooltipTrigger>
                      <TooltipContent
                        sideOffset={20}
                        className=" absolute z-50 w-80  top-5   h-fit text-wrap"
                      >
                        <p className="text-sm">
                          {" "}
                          {isLink
                            ? isLocalOrFileUrl(item.url || "")
                              ? t("Click to copy the link")
                              : undefined
                            : t("download")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </a>
                <div className="w-full truncate ">{item.name}</div>
              </div>
              <div
                className={`text-gray-400 flex items-center justify-end gap-2 w-full max-w-[50%]`}
              >
                {label === item.attachmentId ? (
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      name="label"
                      value={formik.values.label}
                      onChange={(e) =>
                        formik.setFieldValue("label", e.target.value)
                      }
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                      onKeyUp={(e) => {
                        if (e.key == "Enter") {
                          formik.submitForm();
                        }
                      }}
                    />
                    <div className="flex gap-1">
                      <Button
                        className="p-0 mx-2"
                        variant={"none"}
                        onClick={formik.submitForm}
                      >
                        <img src={GreenChackMark} className="w-4 min-w-4" />
                      </Button>
                      <Button
                        className="p-0 mx-2"
                        variant={"none"}
                        onClick={() => {
                          setLabel("");
                          formik.setFieldValue("label", "");
                        }}
                      >
                        <img
                          src={crossNew}
                          className="w-2.5 min-w-2.5 brightness-0"
                        />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="w-full ">
                    {isLink ? (
                      <div className="flex gap-2 items-center justify-end">
                        <span className="text-xs px-2 py-0.5 rounded text-gray-600">
                          {t("link")}
                        </span>
                        {props.allowed && (
                          <Pencil
                            className="w-3 h-3 cursor-pointer text-gray-500 hover:text-primary-500"
                            onClick={() =>
                              setLinkToEdit({
                                id: item.attachmentId,
                                name: item.name,
                                url: item.url,
                              })
                            }
                          />
                        )}
                      </div>
                    ) : item.label ? (
                      <div className="flex gap-2 items-center justify-end relative tooltip w-full ">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="overflow-hidden whitespace-nowrap text-right w-full text-ellipsis truncate max-w-[75%] ml-2 ">
                                {item.label}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              sideOffset={20}
                              className=" absolute z-50 w-40 max-w-40 top-5 -left-48  h-fit text-wrap"
                            >
                              <p className="text-sm"> {item.label}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Pencil
                          className="w-3 h-3 cursor-pointer text-gray-500 hover:text-primary-500"
                          onClick={() => {
                            setLabel(item.attachmentId);
                            formik.setFieldValue("label", item.label);
                          }}
                        />
                      </div>
                    ) : props.allowed ? (
                      <p
                        className="whitespace-nowrap text-primary-500 underline cursor-pointer"
                        onClick={() => setLabel(item.attachmentId)}
                      >
                        {t("add label")}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                )}

                {props.allowed && (
                  <div className="">
                    <Button
                      variant={"none"}
                      className="p-0 w-3 min-w-3"
                      onClick={() => setShowConfirmDelete(item.attachmentId)}
                    >
                      <img src={TrashCanSvg} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      {props.task?.documentAttachments &&
        props.task?.documentAttachments.length > 2 && (
          <div
            className="text-end text-xs cursor-pointer text-primary-500"
            onClick={() => setShowAllAttachment((prev) => !prev)}
          >
            {!showAllAttachment ? t("view_more") : ""}
          </div>
        )}
      <Dialog
        isOpen={showConfirmDelete ? true : false}
        onClose={() => {}}
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCanSvg} className="w-12 m-auto" />
          {t("common_delete_message_file")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              isLoading={removeTaskAttachmentMutation.isPending}
              disabled={removeTaskAttachmentMutation.isPending}
              onClick={() => setShowConfirmDelete(null)}
            >
              {t("cancel")}
            </Button>
            <Button variant={"primary"} onClick={removeConfirm}>
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Edit Link Dialog */}
      <Dialog
        isOpen={!!linkToEdit}
        onClose={() => setLinkToEdit(null)}
        modalClass="rounded-lg w-full max-w-md"
      >
        <div className="flex flex-col gap-4 p-6">
          <h3 className="text-lg font-semibold">{t("edit_link")}</h3>
          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("name")}
            </label>
            <InputText
              name="editLinkName"
              value={linkToEdit?.name || ""}
              onChange={(e) =>
                setLinkToEdit((prev) =>
                  prev ? { ...prev, name: e.target.value } : null,
                )
              }
            />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                checked={editLinkType === "WEB"}
                onChange={() => setEditLinkType("WEB")}
              />
              {t("web_url")}
            </label>

            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                checked={editLinkType === "LOCAL"}
                onChange={() => setEditLinkType("LOCAL")}
              />
              {t("local_network")}
            </label>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("link_url")}
            </label>

            <InputText
              name="editLinkUrl"
              value={linkToEdit?.url || ""}
              placeholder={
                editLinkType === "WEB"
                  ? "https://example.com"
                  : "\\\\User\\share\\file.pdf"
              }
              onChange={(e) =>
                setLinkToEdit((prev) =>
                  prev ? { ...prev, url: e.target.value } : null,
                )
              }
            />

            {linkToEdit?.url &&
              editLinkType === "WEB" &&
              !isWebUrl(linkToEdit.url) && (
                <span className="text-red-500 text-xs">
                  {t("please_enter_valid_url_like_https_or_http")}
                </span>
              )}

            {linkToEdit?.url &&
              editLinkType === "LOCAL" &&
              !isLocalPath(linkToEdit.url) && (
                <span className="text-red-500 text-xs">
                  {t("please_enter_valid_local_path")}
                </span>
              )}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setLinkToEdit(null)}>
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleEditLinkSubmit}
              isLoading={isUpdatingLink}
              disabled={
                !linkToEdit?.name ||
                !linkToEdit?.url ||
                !isValidEditLink ||
                isUpdatingLink
              }
            >
              {t("update")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default TaskAttachment;
